import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { logout, getCurrentUser } from "@/lib/api/auth";
import { useModal } from "@/store/modalStore";

/**
 * 애플리케이션 헤더 컴포넌트
 * @returns {JSX.Element} 헤더 컴포넌트
 */
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation(); // 현재 경로 가져오기
  const modal = useModal();

  // 날씨 정보 상태
  const [weatherInfo, setWeatherInfo] = useState({
    date: new Date().toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    }),
    temperature: "--",
    condition: "로딩중...",
  });

  // 날씨 데이터 가져오기 (Open-Meteo API 사용 - API 키 불필요, JSON 응답 보장)
  useEffect(() => {
    // 날씨 코드 → 한글 설명
    const weatherCodeMap: Record<number, string> = {
      0: "맑음",
      1: "대체로 맑음",
      2: "구름 조금",
      3: "흐림",
      45: "안개",
      48: "짙은 안개",
      51: "가벼운 이슬비",
      53: "이슬비",
      55: "강한 이슬비",
      61: "약한 비",
      63: "비",
      65: "강한 비",
      71: "약한 눈",
      73: "눈",
      75: "강한 눈",
      77: "싸락눈",
      80: "약한 소나기",
      81: "소나기",
      82: "강한 소나기",
      85: "약한 눈",
      86: "눈",
      95: "뇌우",
      96: "약한 우박",
      99: "우박",
    };

    const fetchWeatherData = async () => {
      try {
        // 나주시 좌표: 위도 35.0156, 경도 126.7108
        const response = await fetch(
          "https://api.open-meteo.com/v1/forecast?latitude=35.0156&longitude=126.7108&current_weather=true"
        );

        if (!response.ok) {
          throw new Error(`날씨 API 응답 오류 (HTTP ${response.status})`);
        }

        const data = await response.json();
        const current = data?.current_weather;

        if (!current || typeof current.temperature !== "number") {
          throw new Error("날씨 응답 형식 오류");
        }

        setWeatherInfo({
          date: new Date().toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            weekday: "long",
          }),
          temperature: `${Math.round(current.temperature)}℃`,
          condition: weatherCodeMap[current.weathercode] ?? "정보 없음",
        });
      } catch (error) {
        console.error("날씨 정보 로딩 실패:", error);
        setWeatherInfo((prev) => ({
          ...prev,
          temperature: "--℃",
          condition: "정보 없음",
        }));
      }
    };

    // 컴포넌트 마운트 시 날씨 정보 가져오기
    fetchWeatherData();

    // 30분마다 날씨 정보 업데이트
    const intervalId = setInterval(fetchWeatherData, 30 * 60 * 1000);

    // 클린업 함수
    return () => clearInterval(intervalId);
  }, []);

  // 사용자 정보 가져오기
  const currentUser = getCurrentUser();
  const userInfo = {
    name: currentUser.userName || "사용자",
    role: currentUser.adminGrade || "일반운영자",
  };

  // 메뉴 항목 설정
  const menuItems = [
    { path: "/dashboard", label: "대시보드" },
    { path: "/monitoring", label: "실시간모니터링" },
    { path: "/stat", label: "통계/분석" },
    { path: "/ai", label: "AI예측" },
    { path: "/alert", label: "경고알림관리" },
    { path: "/data", label: "데이터관리" },
    { path: "/complaint", label: "민원관리" },
    { path: "/location", label: "사업장관리" },
    { path: "/device", label: "기기관리" },
    { path: "/admin", label: "시스템관리" },
  ];

  const handleLogout = (e) => {
    e.preventDefault();

    // 로그아웃 확인 모달
    modal.confirm("로그아웃", "정말 로그아웃 하시겠습니까?", () => {
      // 로그아웃 실행
      logout(); // localStorage 정리 및 /login으로 리다이렉트
    });
  };

  return (
    <div className="dbrd_top">
      <div className="dbrd_top_in">
        <div className="dbrd_top_con">
          {/* 상단 정보 영역 */}
          <div className="dv1 cl">
            <div className="w1">
              {weatherInfo.date} {weatherInfo.temperature} <span>{weatherInfo.condition}</span>
            </div>
            <div className="w2">
              <p>
                {userInfo.name}님({userInfo.role})
              </p>
              <a href="#" onClick={handleLogout}>
                <img src="/img/btn_logout01.png" alt="로그아웃" />
              </a>
            </div>
          </div>

          {/* 메뉴 영역 */}
          <div className="dv2">
            <ul className="cl">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    className={location.pathname.startsWith(item.path) ? "active" : ""}
                    data-discover="true"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
